import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { surveyJobBriefOptions, itsqfOptionsForSurvey, skillBriefOptionsForSurvey, findSurveyOption } from '../utils/jobOptions';
import { todayStr, datePlusDays, daysBetween } from '../utils/date';
import { VersionTabs, OldVersionBanner, VersionMemoCompare } from '../components/VersionTools';
import { matchAnyCI } from '../utils/search';
import SmartNumInput from '../components/SmartNumInput';

const fmtN = (v) => { const n = String(v).replace(/[^0-9]/g,''); return n ? Number(n).toLocaleString() : ''; };
const pN = (v) => parseInt(String(v).replace(/[^0-9]/g,'')) || 0;
// 부호 허용 — 수기입력(서비스 패키지 미연결) 행의 음수 단가용
const pNS = (v) => { const s = String(v); const neg = s.trim().startsWith('-') ? -1 : 1; const n = parseInt(s.replace(/[^0-9]/g,''))||0; return neg * n; };

// 합산 행(소계)의 구성 행: 합산 행 바로 위쪽의 같은 묶음 번호 연속 행 (이전 합산 행에서 멈춤)
const compositeMembersIdx = (its, ci) => {
  const b = its[ci].bundle || '';
  const out = [];
  for (let j = ci - 1; j >= 0; j--) {
    if (its[j].item_type === 'composite' || (its[j].bundle || '') !== b) break;
    out.unshift(j);
  }
  return out;
};

export default function EstimateManpowerNewPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get('id');
  const fromId = sp.get('from');
  const copyId = sp.get('copy');
  const seedId = editId || fromId || copyId;
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('서비스견적서 생성·수정');
  // 발행 후 수정(새 버전 없이): 서버가 알려주는 수정 가능 여부(작성자·1시간 이내)/남은 시간, 수정 모드, 남은 초 카운트다운
  const [editWindow, setEditWindow] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editRemain, setEditRemain] = useState(0);

  const [info, setInfo] = useState({
    estimate_date: todayStr(),
    valid_until: datePlusDays(todayStr(), 14),
    end_user: '',
    project_name: '',
    receiver_company: '',
    receiver_name: '',
    notes: '',
    template_id: '',
  });
  const [templates, setTemplates] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [receiverSuggestions, setReceiverSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const [showReceiverSearch, setShowReceiverSearch] = useState(false);
  const [receiverSearchQuery, setReceiverSearchQuery] = useState('');
  const [receiverSearchResults, setReceiverSearchResults] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [items, setItems] = useState([]); // [{ item_type:'manpower', item_name, manpower_grade(=skill_level), headcount, mm, unit_price, itsqf_job_id, itsqf_job_name, job_definition, survey_job_name, service_package_id, service_package_name }]
  const [surveyJobs, setSurveyJobs] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showAddPkg, setShowAddPkg] = useState(false);
  const [pkgSearch, setPkgSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [estId, setEstId] = useState(editId);
  const [draftId, setDraftId] = useState('');
  const [estStatus, setEstStatus] = useState('');
  const [estNumber, setEstNumber] = useState('');
  const [versions, setVersions] = useState([]);
  // 태그: 견적서 한 건에 하나. 저장 전(estId 없음)에 고르면 첫 저장으로 estId 가 생기는 시점에 반영
  const [tagList, setTagList] = useState([]);
  const [tagId, setTagId] = useState('');
  const [pendingTag, setPendingTag] = useState(false);
  useEffect(() => { api.get('/settings/tags/all').then(setTagList).catch(()=>{}); }, []);
  useEffect(() => {
    if (!estId || !pendingTag) return;
    setPendingTag(false);
    api.put(`/estimates/${estId}/tags`, { tag_ids: tagId ? [Number(tagId)] : [] }).catch(e=>alert(e.message));
  }, [estId, pendingTag]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeTag = async (v) => {
    setTagId(v);
    if (!estId) { setPendingTag(true); return; }
    try { await api.put(`/estimates/${estId}/tags`, { tag_ids: v ? [Number(v)] : [] }); }
    catch (e) { alert(e.message); }
  };

  useEffect(() => {
    api.get('/survey-jobs').then(setSurveyJobs).catch(()=>{});
    api.get('/settings/templates/all').then(data => {
      setTemplates(data);
      // 신규 작성일 때만 기본 템플릿 자동 적용
      if (!editId) {
        const def = data.find(t => t.is_default);
        if (def) applyTemplate(def);
      }
    }).catch(()=>setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // info 변경 추적
  const setInfoDirty = (updater) => { setInfo(updater); setIsDirty(true); };
  const setValid = (days) => setInfoDirty(prev => ({...prev, valid_until: datePlusDays(prev.estimate_date, days)}));
  const applyTemplate = (tpl) => {
    setInfoDirty(prev => ({
      ...prev,
      template_id: String(tpl.id),
      notes: tpl.notes || prev.notes,
      valid_until: datePlusDays(prev.estimate_date, tpl.valid_days || 14),
    }));
  };

  // 수신처 자동추천
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!info.receiver_company || info.receiver_company.length < 2 || selectedCustomer) {
      setReceiverSuggestions([]); return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.get(`/customers/search/${encodeURIComponent(info.receiver_company)}`);
        setReceiverSuggestions(data);
      } catch(e) {}
    }, 300);
  }, [info.receiver_company, selectedCustomer]);

  const selectCustomer = (cust) => {
    setInfoDirty(prev => ({...prev, receiver_company: cust.name, receiver_name: cust.contact_name||''}));
    setSelectedCustomer(cust);
    setReceiverSuggestions([]);
  };
  const searchReceiver = async () => {
    if (!receiverSearchQuery) return;
    try { setReceiverSearchResults(await api.get(`/customers/search/${encodeURIComponent(receiverSearchQuery)}`)); } catch(e) {}
  };

  const surveyOptions = useMemo(() => surveyJobBriefOptions(surveyJobs), [surveyJobs]);

  // surveyJobs 로딩 후 — items 의 survey_job_id 가 비어 있으면 itsqf_job_id 로 역검색해서 채움
  useEffect(() => {
    if (!surveyJobs.length) return;
    setItems(prev => prev.map(it => {
      if (it.survey_job_id) return it;
      if (!it.itsqf_job_id) return it;
      for (const sj of surveyJobs) {
        if ((sj.itsqf_jobs||[]).some(j => j.id === it.itsqf_job_id)) {
          return { ...it, survey_job_id: sj.id, survey_job_name: sj.survey_job };
        }
      }
      return it;
    }));
  }, [surveyJobs]);

  useEffect(() => {
    if (!seedId) return;
    api.get(`/estimates/${seedId}`).then(est => {
      const srcDate = est.estimate_date?.slice(0,10) || '';
      const srcValid = est.valid_until?.slice(0,10) || '';
      let estimateDate = srcDate;
      let validUntil = srcValid;
      // 후속버전: 작성일자를 항상 오늘로, 유효기간은 원본 유효일수를 유지해 오늘 기준으로 재조정
      if (fromId && !editId) {
        estimateDate = todayStr();
        let span = 14;
        if (srcDate && srcValid) { const d = daysBetween(srcDate, srcValid); if (d > 0) span = d; }
        validUntil = datePlusDays(estimateDate, span);
      }
      setInfo({
        estimate_date: estimateDate,
        valid_until: validUntil,
        end_user: est.end_user || '',
        project_name: est.project_name || '',
        receiver_company: est.receiver_company || '',
        receiver_name: est.receiver_name || '',
        notes: est.notes || '',
        template_id: est.template_id || '',
      });
      setItems((est.items || []).map(it => ({
        item_type: it.item_type === 'composite' ? 'composite' : 'manpower',
        bundle: it.bundle || '',
        item_name: it.item_name || it.description || '',
        manpower_grade: it.manpower_grade || '',
        itsqf_job_name: it.itsqf_job_name || '',
        job_definition: it.job_definition || '',
        itsqf_job_id: it.itsqf_job_id || null,
        survey_job_id: null, // 아래 useEffect 에서 surveyJobs 로딩 후 채움
        survey_job_name: it.survey_job_name || '',
        headcount: String(it.headcount || 1),
        mm: String(it.mm || 0),
        unit_price: String(it.unit_price || 0),
        service_package_id: it.service_package_id || null,
        service_package_name: it.service_package_name || '',
        quantity: it.item_type === 'composite' ? String(it.quantity || 1) : 1,
      })));
      // 편집(`id`): 원본 그대로 / 후속버전(`from`): 새 draft 가 만들어질 때까지 estId 비워둠
      if (editId) {
        setEditWindow(est.edit_window || null);
        setEditRemain(est.edit_window?.remaining_seconds || 0);
        setDraftId(est.draft_id || '');
        setEstStatus(est.status || '');
        setEstNumber(est.estimate_number || '');
        setVersions(est.versions || []);
        setTagId(est.tags?.[0]?.id ? String(est.tags[0].id) : '');
      } else {
        setDraftId(''); setEstStatus(''); setEstNumber(''); setVersions([]);
      }
    }).catch(e => alert(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedId]);

  // 패키지 모달 열 때 목록 로드
  useEffect(() => {
    if (!showAddPkg) return;
    api.get('/service-packages').then(setPackages).catch(()=>{});
  }, [showAddPkg]);

  const filteredPkgs = useMemo(() => {
    if (!pkgSearch) return packages;
    return packages.filter(p => matchAnyCI(pkgSearch, p.name, p.code));
  }, [packages, pkgSearch]);

  const upItem = (i, key, val) => setItems(items.map((it,j) => j===i ? {...it,[key]:val} : it));
  const removeItem = (i) => setItems(items.filter((_,j)=>j!==i));
  const moveItem = (i, dir) => {
    const j = i+dir; if (j<0 || j>=items.length) return;
    const u = [...items]; [u[i],u[j]] = [u[j],u[i]]; setItems(u);
  };

  // 다음 묶음 번호
  const nextBundle = () => {
    const nums = items.map(it => parseInt(it.bundle)||0);
    return String((nums.length ? Math.max(...nums) : 0) + 1);
  };

  // 묶음 순서 (items 에 첫 등장한 순)
  const bundleOrder = useMemo(() => {
    const order = [];
    items.forEach(it => { if (it.bundle && !order.includes(it.bundle)) order.push(it.bundle); });
    return order;
  }, [items]);

  // 묶음 단위 이동 — 블록 전체를 swap
  const moveBundle = (bundle, dir) => {
    const order = [...bundleOrder];
    const idx = order.indexOf(bundle);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= order.length) return;
    [order[idx], order[swap]] = [order[swap], order[idx]];
    const grouped = {};
    const orphans = [];
    items.forEach(it => {
      if (it.bundle) { (grouped[it.bundle] ||= []).push(it); }
      else orphans.push(it);
    });
    setItems([...order.flatMap(b => grouped[b] || []), ...orphans]);
  };

  // 패키지이름 일괄 변경 (같은 묶음 모든 행)
  const updPackageName = (bundle, name) => {
    setItems(items.map(it => it.bundle === bundle ? { ...it, service_package_name: name } : it));
  };

  const addManualRow = () => setItems([...items, {
    item_type: 'manpower',
    bundle: nextBundle(),
    item_name: '',
    manpower_grade: '',
    itsqf_job_name: '',
    job_definition: '',
    itsqf_job_id: null,
    survey_job_id: null,
    survey_job_name: '',
    headcount: '1',
    mm: '1',
    unit_price: '0',
    service_package_id: null,
    service_package_name: '',
    quantity: 1,
  }]);

  const addPackage = async (pkg) => {
    try {
      const full = await api.get(`/service-packages/${pkg.id}`);
      const bundle = nextBundle();
      const newRows = (full.tasks || []).map(t => ({
        item_type: 'manpower',
        bundle,
        item_name: t.task_name || '',
        manpower_grade: t.skill_level || '',
        itsqf_job_name: t.itsqf_job_name || '',
        job_definition: t.job_definition || '',
        itsqf_job_id: t.itsqf_job_id || null,
        survey_job_id: t.survey_job_id || null,
        survey_job_name: t.survey_job_name || '',
        headcount: String(t.default_headcount || 1),
        mm: String(t.default_mm || 0),
        unit_price: String(t.default_monthly_rate || 0),
        service_package_id: full.id,
        service_package_name: full.name,
        quantity: 1,
      }));
      setItems([...items, ...newRows]);
      setShowAddPkg(false);
      setPkgSearch('');
    } catch (e) { alert(e.message); }
  };

  // 조사용직무 선택 시 — ITSQF/등급/단가 리셋
  const setSurvey = (i, surveyJobId) => {
    const opt = findSurveyOption(surveyOptions, surveyJobId ? parseInt(surveyJobId) : null);
    setItems(items.map((it,j) => j===i ? {
      ...it,
      survey_job_id: opt?.survey_job_id || null,
      survey_job_name: opt?.survey_job || '',
      itsqf_job_id: null,
      itsqf_job_name: '',
      job_definition: '',
      manpower_grade: '',
      unit_price: '0',
    } : it));
  };

  // ITSQF 선택 시 — itsqf snapshot 채움 (등급은 유지)
  const setItsqf = (i, itsqfJobId) => {
    const it = items[i];
    const sOpt = findSurveyOption(surveyOptions, it.survey_job_id);
    const itsqfOpt = (sOpt?.itsqf_jobs||[]).find(j => j.id === (itsqfJobId ? parseInt(itsqfJobId) : null));
    setItems(items.map((tt,j) => j===i ? {
      ...tt,
      itsqf_job_id: itsqfOpt?.id || null,
      itsqf_job_name: itsqfOpt?.itsqf_job || '',
      job_definition: itsqfOpt?.job_definition || '',
    } : tt));
  };

  // 기술등급 선택 시 — 단가 자동 채움
  const setSkillLevel = (i, skillLevel) => {
    const it = items[i];
    const sOpt = findSurveyOption(surveyOptions, it.survey_job_id);
    const rate = sOpt?.rates?.find(r => r.skill_level === skillLevel);
    setItems(items.map((tt,j) => j===i ? {
      ...tt,
      manpower_grade: skillLevel,
      unit_price: rate ? String(rate.monthly_rate || 0) : tt.unit_price,
    } : tt));
  };

  const rowSub = (it) => Math.round((pNS(it.unit_price)) * (parseFloat(it.mm)||0) * (parseInt(it.headcount)||1));
  // 합산 행(소계): 바로 위 같은 묶음 번호의 연속 행(구성 행)의 제안소계 합 = 합산 행의 단가. 구성 행은 총계에서 제외.
  const memberSet = new Set();
  items.forEach((c, ci) => { if (c.item_type === 'composite') compositeMembersIdx(items, ci).forEach(j => memberSet.add(j)); });
  const compositeUnit = (ci, its = items) => compositeMembersIdx(its, ci).reduce((a, j) => a + rowSub(its[j]), 0);
  const compositeQty = (c) => parseInt(c.quantity) || 1;
  const subtotal = items.reduce((s, it, i) => s + (it.item_type === 'composite' ? compositeUnit(i) * compositeQty(it) : memberSet.has(i) ? 0 : rowSub(it)), 0);
  const vat = Math.round(subtotal * 0.1);
  const total = subtotal + vat;
  const totalMM = items.reduce((s, it) => s + (it.item_type === 'composite' ? 0 : (parseFloat(it.mm)||0)), 0);

  const cleanItems = () => items.map((it, ci) => it.item_type === 'composite' ? ({
    item_type: 'composite',
    bundle: it.bundle || null,
    item_name: it.item_name || '',
    description: it.item_name || '',
    unit_price: compositeUnit(ci),
    quantity: compositeQty(it),
    cost_per_unit: 0,
    service_package_id: it.service_package_id || null,
    service_package_name: it.service_package_name || null,
  }) : ({
    item_type: 'manpower',
    bundle: it.bundle || null,
    item_name: it.item_name || '',
    description: it.item_name || '',
    manpower_grade: it.manpower_grade || null,
    itsqf_job_name: it.itsqf_job_name || null,
    job_definition: it.job_definition || null,
    itsqf_job_id: it.itsqf_job_id || null,
    survey_job_name: it.survey_job_name || null,
    headcount: parseInt(it.headcount) || 1,
    mm: parseFloat(it.mm) || 0,
    unit_price: pNS(it.unit_price),
    quantity: 1,
    cost_per_unit: 0,
    service_package_id: it.service_package_id || null,
    service_package_name: it.service_package_name || null,
  }));

  // 읽기전용 — published / unapproved_published / pending_approval 인 견적은 수정 불가
  const isPublishedSt = estStatus === 'published' || estStatus === 'unapproved_published';
  const canEditPublished = isPublishedSt && !!editWindow?.editable && editRemain > 0; // 발행 후 1시간 이내 수정 가능
  const ro = (isPublishedSt && !editing) || estStatus === 'pending_approval';

  const buildBody = () => ({
    ...info,
    business_type: 'manpower',
    vat_separate: true,
    vat_input_mode: 'separate',
    show_consumer_price: false,
    items: cleanItems(),
  });

  const syncUrlToId = (newId) => {
    if (typeof window === 'undefined' || !newId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('id', newId);
    url.searchParams.delete('from');
    window.history.replaceState({}, '', url.toString());
  };

  // 핵심 저장 — silent 모드일 때는 alert 없이 id 반환
  const saveCore = async (silent = false) => {
    if (items.length === 0) { if (!silent) alert('항목을 1개 이상 추가하세요.'); return null; }
    const body = buildBody();
    if (estId) {
      await api.put(`/estimates/${estId}`, body);
      setIsDirty(false);
      return estId;
    }
    // 후속버전 모드: next-version 으로 draft 생성 후 PUT
    if (fromId) {
      const r = await api.post(`/estimates/${fromId}/next-version`, {});
      await api.put(`/estimates/${r.id}`, body);
      setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft'); setEstNumber('');
      setIsDirty(false);
      syncUrlToId(r.id);
      return r.id;
    }
    const r = await api.post('/estimates', body);
    setEstId(r.id);
    setDraftId(r.draft_id);
    setEstStatus('draft');
    setIsDirty(false);
    syncUrlToId(r.id);
    return r.id;
  };

  const save = async () => {
    setSaving(true);
    try {
      const id = await saveCore(false);
      if (id) alert(estId ? '저장되었습니다.' : '임시저장되었습니다.');
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  // 발행
  const publishErrors = [];
  if (!info.estimate_date) publishErrors.push('견적일자');
  if (!info.valid_until) publishErrors.push('유효기간');
  if (!info.receiver_company) publishErrors.push('수신처');
  if (!info.receiver_name) publishErrors.push('수신자');
  if (items.length === 0) publishErrors.push('견적 항목 (1개 이상)');

  // 수정 가능 시간 카운트다운 (1초). 0 이 되면 읽기전용으로 되돌리기 위해 새로고침
  useEffect(() => {
    if (!editWindow?.editable) return undefined;
    const t = setInterval(() => setEditRemain(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [editWindow]);
  useEffect(() => {
    if (editing && editRemain === 0) { alert(`수정 가능 시간(발행 후 ${editWindow?.window_minutes ?? 60}분)이 지났습니다. 저장하지 않은 변경 내용은 사라집니다.\n계속 수정하려면 후속 버전을 생성하세요.`); window.location.reload(); }
  }, [editing, editRemain]);
  const fmtRemain = (sec) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  // 발행 후 수정 저장: 새 버전 없이 같은 견적서를 갱신 (서버가 1시간·승인정책을 다시 검증)
  const doEditSave = async () => {
    if (publishErrors.length > 0) { alert('저장할 수 없습니다. 다음 항목을 입력해 주세요:\n\n• ' + publishErrors.join('\n• ')); return; }
    setSaving(true);
    try {
      await api.put(`/estimates/${estId}`, buildBody());
      setIsDirty(false);
      alert('수정되었습니다.');
      window.location.reload();
    } catch (e) { alert(e.message); setSaving(false); }
  };
  const cancelEdit = () => {
    if (isDirty && !window.confirm('수정 중인 내용이 사라집니다. 취소할까요?')) return;
    setIsDirty(false);
    window.location.reload();
  };

  const doPublish = async () => {
    if (publishErrors.length > 0) { alert('발행할 수 없습니다. 다음 항목을 입력해 주세요:\n\n• ' + publishErrors.join('\n• ')); return; }
    if (!window.confirm('발행하시겠습니까? 발행 후에는 수정할 수 없습니다.')) return;
    setPublishing(true);
    try {
      let cid = estId;
      if (!cid) {
        cid = await saveCore(true);
        if (!cid) return;
      } else if (isDirty) {
        await saveCore(true);
      }
      const r = await api.post(`/estimates/${cid}/publish`, {});
      if (r.status === 'pending_approval') alert('승인 요청이 전송되었습니다.');
      else alert(`발행 완료! 견적번호: ${r.estimate_number}`);
      navigate('/service-estimate');
    } catch (e) { alert(e.message); } finally { setPublishing(false); }
  };

  const previewPdf = async () => {
    try {
      let id = estId;
      if (!id) {
        if (items.length === 0) { alert('항목을 1개 이상 추가하세요.'); return; }
        setSaving(true);
        id = await saveCore(true);
        setSaving(false);
        if (!id) return;
      } else if (isDirty) {
        // 변경분이 있으면 미리보기 전 저장
        setSaving(true);
        await saveCore(true);
        setSaving(false);
      }
      window.open(`/estimates/print?id=${id}&preview=1`, '_blank');
    } catch (e) { setSaving(false); alert(e.message); }
  };

  // 변경 감지 (초기 로드 제외) — info/items 가 바뀌면 isDirty 표시
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!loadedRef.current) { loadedRef.current = true; return; }
    setIsDirty(true);
  }, [info, items]);

  // 자동저장 (30초)
  const autoSaveRef = useRef(null);
  autoSaveRef.current = { info, items, estId, isDirty, fromId, ro, editing };
  useEffect(() => {
    const timer = setInterval(async () => {
      const s = autoSaveRef.current;
      if (s.ro || s.editing || !s.isDirty || s.items.length === 0) return; // 발행 후 수정 중에는 자동저장하지 않음
      try {
        const body = {
          ...s.info,
          business_type: 'manpower',
          vat_separate: true,
          vat_input_mode: 'separate',
          show_consumer_price: false,
          items: s.items.map(it => ({
            item_type: 'manpower',
            bundle: it.bundle || null,
            item_name: it.item_name || '',
            description: it.item_name || '',
            manpower_grade: it.manpower_grade || null,
            itsqf_job_name: it.itsqf_job_name || null,
            job_definition: it.job_definition || null,
            itsqf_job_id: it.itsqf_job_id || null,
            survey_job_name: it.survey_job_name || null,
            headcount: parseInt(it.headcount) || 1,
            mm: parseFloat(it.mm) || 0,
            unit_price: pNS(it.unit_price),
            quantity: 1,
            cost_per_unit: 0,
            service_package_id: it.service_package_id || null,
            service_package_name: it.service_package_name || null,
          })),
        };
        if (s.estId) {
          await api.put(`/estimates/${s.estId}`, body);
        } else if (s.fromId) {
          const r = await api.post(`/estimates/${s.fromId}/next-version`, {});
          await api.put(`/estimates/${r.id}`, body);
          setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft'); setEstNumber('');
          syncUrlToId(r.id);
        } else {
          const r = await api.post('/estimates', body);
          setEstId(r.id); setDraftId(r.draft_id); setEstStatus('draft');
          syncUrlToId(r.id);
        }
        setIsDirty(false);
      } catch (e) { /* 자동저장 실패는 silent */ }
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* 우측 상단 액션 바 — 별도 카드 없이 가볍게 */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {estNumber && <span className="badge badge-blue">{estNumber}</span>}
          {draftId && !estNumber && <span className="badge badge-gray">{draftId}</span>}
          <VersionTabs versions={versions} currentId={editId} basePath="/service-estimate/new" />
          {estStatus === 'pending_approval' && <span className="badge badge-yellow">승인대기</span>}
          {estStatus === 'published' && <span className="badge badge-blue">발행</span>}
          {estStatus === 'unapproved_published' && <span className="badge badge-orange">미승인발행</span>}
          {fromId && !estId && <span className="badge badge-blue">후속버전 작성 중</span>}
          {isDirty && <span style={{fontSize:12,color:'#f59e0b'}}>● 저장되지 않은 변경사항</span>}
          {editing && <span className="badge badge-orange">발행 후 수정 중 · {fmtRemain(editRemain)} 남음</span>}
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-outline" onClick={()=>navigate('/service-estimate')}>목록</button>
          {!editing && <button className="btn btn-outline" onClick={previewPdf} disabled={publishing}>👁 미리보기</button>}
          {ro && canEditPublished && <button className="btn btn-outline" style={{borderColor:'#f59e0b',color:'#b45309'}} title={`새 버전을 만들지 않고 이 견적서를 직접 수정합니다. 발행 후 ${editWindow?.window_minutes ?? 60}분 이내에만 가능합니다.`} onClick={()=>setEditing(true)}>✏ 수정 ({fmtRemain(editRemain)} 남음)</button>}
          {ro && estId && (estStatus==='published' || estStatus==='unapproved_published') && (
            <button className="btn btn-primary" onClick={()=>{ window.location.href = `/service-estimate/new?from=${estId}`; }}>📑 후속버전</button>
          )}
          {editing && <button className="btn btn-outline" onClick={cancelEdit} disabled={saving}>수정 취소</button>}
          {editing && <button className="btn btn-primary" onClick={doEditSave} disabled={saving}>{saving?'저장 중...':'💾 수정 저장'}</button>}
          {!ro && !editing && (
            <>
              {canEdit && <button className="btn btn-outline" onClick={save} disabled={saving||publishing}>{saving?'저장 중...':'💾 임시저장'}</button>}
              {canEdit && <button className="btn btn-primary" onClick={doPublish} disabled={saving||publishing} style={publishErrors.length>0?{opacity:0.7}:{}}>{publishing?'발행 중...':'📤 발행'}</button>}
            </>
          )}
        </div>
      </div>

      <OldVersionBanner versions={versions} currentId={editId} basePath="/service-estimate/new" />

      <div className="card">
        <div className="card-title" style={{marginBottom:12}}>기본 정보</div>
        <div className="grid-2">
          <div className="form-group"><label className="form-label">견적일자</label>
            <input className="form-input" type="date" value={info.estimate_date} onChange={e=>setInfoDirty({...info,estimate_date:e.target.value})} disabled={ro} /></div>
          <div className="form-group">
            <label className="form-label">유효기간</label>
            <div style={{display:'flex',gap:6}}>
              <input className="form-input" type="date" style={{flex:1}} value={info.valid_until} onChange={e=>setInfoDirty({...info,valid_until:e.target.value})} disabled={ro} />
              <button type="button" className="btn btn-outline btn-sm" onClick={()=>setValid(7)} disabled={ro}>1주</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={()=>setValid(14)} disabled={ro}>2주</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={()=>setValid(30)} disabled={ro}>1개월</button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">견적서 양식</label>
            <select className="form-input" value={info.template_id||''} disabled={ro} onChange={e=>{
              const tpl = templates.find(t=>String(t.id)===e.target.value);
              if (tpl) applyTemplate(tpl);
              else setInfoDirty({...info, template_id:''});
            }}>
              <option value="">— 선택 안 함 —</option>
              {templates.map(t=> <option key={t.id} value={t.id}>{t.name}{t.is_default?' (기본)':''}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">발주처</label>
            <input className="form-input" value={info.end_user} onChange={e=>setInfoDirty({...info,end_user:e.target.value})} disabled={ro} placeholder="PDF의 '최종사용자' 항목에 표시됩니다" /></div>
          <div className="form-group"><label className="form-label">사업명</label>
            <input className="form-input" value={info.project_name} onChange={e=>setInfoDirty({...info,project_name:e.target.value})} disabled={ro} /></div>
          <div className="form-group"><label className="form-label">태그</label>
            <select className="form-input" value={tagId} onChange={e=>changeTag(e.target.value)} disabled={!canEdit}>
              <option value="">-</option>{tagList.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select></div>
        </div>
      </div>

      <div className="card">
        <div className="card-title" style={{marginBottom:12}}>견적 수신자</div>
        <div className="grid-2">
          <div className="form-group" style={{position:'relative'}}>
            <label className="form-label">수신처 (회사명)</label>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" style={{flex:1}} value={info.receiver_company}
                onChange={e=>{ setInfoDirty({...info,receiver_company:e.target.value}); setSelectedCustomer(null); }}
                disabled={ro}
                placeholder="수신처를 입력하세요" />
              <button type="button" className="btn btn-outline btn-sm" onClick={()=>{setShowReceiverSearch(true);setReceiverSearchQuery('');setReceiverSearchResults([]);}} disabled={ro}>🔍 검색</button>
            </div>
            {receiverSuggestions.length > 0 && (
              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:7,padding:'7px 11px',fontSize:12,marginTop:5,color:'#92400e'}}>
                유사한 등록 수신처: {receiverSuggestions.map((s,i)=>(
                  <span key={s.id}>{i>0 && ', '}<span style={{color:'#3b82f6',cursor:'pointer',fontWeight:600}} onClick={()=>selectCustomer(s)}>{s.name}</span></span>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">수신자 (담당자)</label>
            <input className="form-input" value={info.receiver_name} onChange={e=>setInfoDirty({...info,receiver_name:e.target.value})} disabled={ro} placeholder="담당자명" />
            <div className="text-muted" style={{marginTop:4,fontSize:11}}>등록된 수신처 선택 시 담당자가 자동 입력됩니다</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{marginBottom:10}}>
          <div className="card-title">견적 항목</div>
          {!ro && (
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowAddPkg(true)}>📦 서비스 패키지 추가</button>
              <button className="btn btn-outline btn-sm" onClick={addManualRow}>✎ 수기 행 추가</button>
              <button className="btn btn-outline btn-sm" title="바로 위쪽의 같은 묶음 번호 행들을 합산하는 소계 행을 추가합니다. 합산 단가 × 수량이 제안소계가 됩니다." onClick={()=>setItems([...items,{item_type:'composite',item_name:'소계',unit_price:'0',quantity:'1',bundle:'',service_package_name:''}])}>∑ 소계 행</button>
            </div>
          )}
        </div>
        <div style={{overflowX:'auto'}}><table style={{fontSize:12,borderCollapse:'collapse'}}>
          <thead>
            <tr>
              <th style={{width:40}}>묶음</th>
              <th style={{width:28}}>NO</th>
              <th style={{width:110}}>패키지이름</th>
              <th style={{minWidth:240}}>품명</th>
              <th style={{width:100}}>조사용직무</th>
              <th style={{width:110}}>ITSQF 직무</th>
              <th style={{width:90}}>인력등급</th>
              <th style={{width:48}}>인원</th>
              <th style={{width:78}}>M/M</th>
              <th style={{width:116}}>월단가</th>
              <th style={{width:110,textAlign:'right'}}>제안소계</th>
              <th style={{width:96}}>액션</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
            // 소계 행이 있으면 구성 행들의 제안소계 칸을 소계 행까지 하나로 병합
            const mergeSub = {}, skipSub = new Set();
            items.forEach((c, ci) => {
              if (c.item_type !== 'composite') return;
              const ms = compositeMembersIdx(items, ci);
              if (ms.length === 0) return;
              mergeSub[ms[0]] = { span: ms.length + 1, ci };
              ms.slice(1).forEach(j => skipSub.add(j));
              skipSub.add(ci);
            });
            return items.map((it,i) => {
              const mergeInfo = mergeSub[i]; // 구성 행들의 제안소계 칸을 소계 행까지 병합 (소계 × 수량만 표시)
              if (it.item_type === 'composite') {
                const unit = compositeUnit(i);
                const th = {padding:'4px 3px',background:'#eef2f7'};
                const inpS = {padding:'4px 6px',fontSize:11};
                const nMembers = compositeMembersIdx(items, i).length;
                const isBundleFirstC = !!it.bundle && !items.slice(0,i).some(p => p.bundle === it.bundle);
                return (
                  <tr key={i}>
                    <td style={th}><input className="form-input" style={{...inpS,textAlign:'center'}} value={it.bundle||''} onChange={e=>upItem(i,'bundle',e.target.value)} disabled={ro} /></td>
                    <td style={th}></td>
                    <td style={th}>{isBundleFirstC && <input className="form-input" style={{...inpS,fontWeight:700}} value={it.service_package_name||''} onChange={e=>updPackageName(it.bundle, e.target.value)} disabled={ro} placeholder="패키지 이름" />}</td>
                    <td style={th}>
                      <textarea className="form-input" rows={1} style={{...inpS,fontWeight:700,textAlign:'right'}} value={it.item_name||''} onChange={e=>upItem(i,'item_name',e.target.value)} disabled={ro} placeholder="소계 *" />
                      <div style={{fontSize:10,textAlign:'right',color:nMembers?'#64748b':'#ef4444',marginTop:2}}>{nMembers ? `바로 위 ${nMembers}개 행 합산` : '합산할 행이 없습니다 (바로 위에 같은 묶음 번호의 행이 있어야 합니다)'}</div>
                    </td>
                    <td style={th} colSpan={3}><span className="badge badge-purple">소계</span></td>
                    <td style={th}><SmartNumInput className="form-input" style={{...inpS,textAlign:'center'}} value={it.quantity} onValueChange={v=>upItem(i,'quantity',v)} disabled={ro} /><div style={{fontSize:10,color:'#94a3b8',textAlign:'center'}}>수량</div></td>
                    <td style={th}></td>
                    <td style={{...th,textAlign:'right',fontWeight:700}}>₩{unit.toLocaleString()}</td>
                    {!skipSub.has(i) && <td style={{...th,textAlign:'right',fontWeight:700}}>{unit!==0?`₩${(unit*compositeQty(it)).toLocaleString()}`:'-'}</td>}
                    <td style={{...th,textAlign:'center',whiteSpace:'nowrap'}}>
                      {!ro && <>
                        <button title="행 위로" className="btn btn-outline btn-sm" style={{padding:'2px 4px',marginLeft:2}} onClick={()=>moveItem(i,-1)} disabled={i===0}>↑</button>
                        <button title="행 아래로" className="btn btn-outline btn-sm" style={{padding:'2px 4px',marginLeft:1}} onClick={()=>moveItem(i,1)} disabled={i===items.length-1}>↓</button>
                        <button title="행 삭제" className="btn btn-danger btn-sm" style={{padding:'2px 4px',marginLeft:1}} onClick={()=>removeItem(i)}>✕</button>
                      </>}
                    </td>
                  </tr>
                );
              }
              const sOpt = findSurveyOption(surveyOptions, it.survey_job_id);
              const itsqfOpts = itsqfOptionsForSurvey(sOpt);
              const skillOpts = skillBriefOptionsForSurvey(sOpt);
              const sub = Math.round((pNS(it.unit_price)) * (parseFloat(it.mm)||0) * (parseInt(it.headcount)||1));
              const isBundleFirst = !!it.bundle && !items.slice(0,i).some(p => p.bundle === it.bundle);
              const bIdx = bundleOrder.indexOf(it.bundle);
              const isLastBundle = bIdx === bundleOrder.length - 1;
              const cellPad = {padding:'4px 3px'};
              const inpStyle = {padding:'4px 6px',fontSize:11};
              return (
                <tr key={i}>
                  <td style={cellPad}>
                    <input className="form-input" style={{...inpStyle,textAlign:'center'}} value={it.bundle||''}
                      onChange={e=>upItem(i,'bundle',e.target.value)} disabled={ro} placeholder="" />
                  </td>
                  <td style={{...cellPad,textAlign:'center',color:'#94a3b8'}}>{i+1}</td>
                  <td style={cellPad}>
                    {isBundleFirst ? (
                      <input className="form-input" style={{...inpStyle,fontWeight:700}}
                        value={it.service_package_name||''}
                        onChange={e=>updPackageName(it.bundle, e.target.value)} disabled={ro}
                        placeholder="패키지 이름" />
                    ) : null}
                  </td>
                  <td style={cellPad}>
                    <textarea className="form-input" rows={3} style={inpStyle}
                      value={it.item_name} onChange={e=>upItem(i,'item_name',e.target.value)} disabled={ro}
                      placeholder={'▶ 항목 제목\n - 세부설명'} />
                  </td>
                  <td style={cellPad}>
                    <select className="form-input" style={inpStyle} value={it.survey_job_id||''} onChange={e=>setSurvey(i, e.target.value)} disabled={ro}>
                      <option value="">— 선택 —</option>
                      {surveyOptions.map(o => <option key={o.survey_job_id} value={o.survey_job_id}>{o.label}</option>)}
                    </select>
                  </td>
                  <td style={cellPad}>
                    <select className="form-input" style={inpStyle} value={it.itsqf_job_id||''} onChange={e=>setItsqf(i, e.target.value)} disabled={ro||!sOpt}>
                      <option value="">— 선택 —</option>
                      {itsqfOpts.map(o => <option key={o.itsqf_job_id} value={o.itsqf_job_id}>{o.label}</option>)}
                    </select>
                  </td>
                  <td style={cellPad}>
                    <select className="form-input" style={inpStyle} value={it.manpower_grade||''} onChange={e=>setSkillLevel(i,e.target.value)} disabled={ro||!sOpt}>
                      <option value="">— 선택 —</option>
                      {skillOpts.map(s => <option key={s.skill_level} value={s.skill_level}>{s.label}</option>)}
                    </select>
                  </td>
                  <td style={cellPad}><SmartNumInput className="form-input" style={{...inpStyle,textAlign:'center'}} value={it.headcount} onValueChange={v=>upItem(i,'headcount',v)} disabled={ro} /></td>
                  <td style={cellPad}><SmartNumInput className="form-input" style={{...inpStyle,textAlign:'center'}} value={it.mm} onValueChange={v=>upItem(i,'mm',v)} disabled={ro} /></td>
                  <td style={cellPad}>{(() => {
                    const allowNeg = !it.service_package_id; // 수기 행만 음수 허용
                    const handleChange = allowNeg
                      ? (e) => {
                          const cleaned = e.target.value.replace(/[^0-9-]/g, '');
                          const neg = cleaned.startsWith('-');
                          const digits = cleaned.replace(/-/g, '');
                          upItem(i, 'unit_price', (neg ? '-' : '') + digits);
                        }
                      : (e) => upItem(i, 'unit_price', String(pN(e.target.value)));
                    const displayValue = allowNeg
                      ? (() => {
                          const s = String(it.unit_price ?? '');
                          const neg = s.startsWith('-');
                          const digits = s.replace(/[^0-9]/g, '');
                          if (!digits) return neg ? '-' : '';
                          return (neg ? '-' : '') + Number(digits).toLocaleString();
                        })()
                      : fmtN(it.unit_price);
                    return <input className="form-input" style={{...inpStyle,textAlign:'right'}}
                      value={displayValue}
                      onChange={handleChange}
                      disabled={ro} placeholder="자동" title={allowNeg ? '음수 가능' : ''} />;
                  })()}</td>
                  {mergeInfo ? (
                    <td rowSpan={mergeInfo.span} style={{...cellPad,textAlign:'right',fontWeight:700,verticalAlign:'bottom'}}>{(() => { const v = compositeUnit(mergeInfo.ci) * compositeQty(items[mergeInfo.ci]); return v!==0?`₩${v.toLocaleString()}`:'-'; })()}</td>
                  ) : !skipSub.has(i) && (
                    <td style={{...cellPad,textAlign:'right',fontWeight:700}}>{sub!==0?`₩${sub.toLocaleString()}`:'-'}</td>
                  )}
                  <td style={{...cellPad,textAlign:'center',whiteSpace:'nowrap'}}>
                    {!ro && isBundleFirst && (
                      <>
                        <button title="묶음 전체 위로" className="btn btn-outline btn-sm" style={{padding:'2px 4px',fontSize:10}} onClick={()=>moveBundle(it.bundle,-1)} disabled={bIdx<=0}>⇧</button>
                        <button title="묶음 전체 아래로" className="btn btn-outline btn-sm" style={{padding:'2px 4px',fontSize:10,marginLeft:1}} onClick={()=>moveBundle(it.bundle,1)} disabled={isLastBundle}>⇩</button>
                      </>
                    )}
                    {!ro && <>
                      <button title="행 위로" className="btn btn-outline btn-sm" style={{padding:'2px 4px',marginLeft:2}} onClick={()=>moveItem(i,-1)} disabled={i===0}>↑</button>
                      <button title="행 아래로" className="btn btn-outline btn-sm" style={{padding:'2px 4px',marginLeft:1}} onClick={()=>moveItem(i,1)} disabled={i===items.length-1}>↓</button>
                      <button title="행 삭제" className="btn btn-danger btn-sm" style={{padding:'2px 4px',marginLeft:1}} onClick={()=>removeItem(i)}>✕</button>
                    </>}
                  </td>
                </tr>
              );
            }); })()}
            {items.length===0 && <tr><td colSpan={12} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>서비스 패키지 또는 수기 행을 추가하세요.</td></tr>}
            {items.length > 0 && (
              <>
                <tr style={{background:'#f8fafc',borderTop:'2px solid #1e293b'}}>
                  <td colSpan={8} style={{textAlign:'right',fontWeight:700,padding:10}}>공급가액 총 계 (부가세 별도)</td>
                  <td style={{textAlign:'center',fontWeight:700,padding:10}}>{totalMM.toFixed(2)}</td>
                  <td></td>
                  <td style={{textAlign:'right',fontWeight:800,fontSize:15,padding:10}}>₩{subtotal.toLocaleString()}</td>
                  <td></td>
                </tr>
                <tr style={{background:'#f8fafc'}}>
                  <td colSpan={10} style={{textAlign:'right',color:'#64748b',padding:'8px 10px'}}>부가가치세</td>
                  <td style={{textAlign:'right',color:'#64748b',padding:'8px 10px'}}>₩{vat.toLocaleString()}</td>
                  <td></td>
                </tr>
                <tr style={{background:'#eff6ff'}}>
                  <td colSpan={10} style={{textAlign:'right',fontWeight:800,padding:10}}>부가세 포함 총 계</td>
                  <td style={{textAlign:'right',fontWeight:800,fontSize:17,color:'#3b82f6',padding:10}}>₩{total.toLocaleString()}</td>
                  <td></td>
                </tr>
              </>
            )}
          </tbody>
        </table></div>
      </div>

      <div className="card">
        <div className="card-title" style={{marginBottom:12}}>비고 (REMARK)</div>
        <textarea className="form-input" rows={3} value={info.notes} onChange={e=>setInfoDirty({...info,notes:e.target.value})} disabled={ro} />
      </div>

      {/* 버전 이력 */}
      {versions.length>0 && (
        <div className="card">
          <div className="card-header"><div className="card-title" style={{marginBottom:0}}>버전 이력</div></div>
          <VersionMemoCompare versions={versions} currentId={editId} canEditNote={!!editId && canEdit}
            onNoteSaved={(id,note)=>setVersions(vs=>vs.map(x=>x.id===id?{...x,version_note:note}:x))} />
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))',gap:12}}>
            {versions.map(v=>{
              const isCurrent = v.id === parseInt(editId);
              const names = v.item_names_top || [];
              const count = v.item_count || 0;
              const itemSummary = count === 0 ? '항목 없음'
                : count <= names.length ? names.join(' / ')
                : `${names.join(' / ')} 외 ${count - names.length}건`;
              return (
                <div key={v.id}
                  onClick={()=>{ if(!isCurrent) window.location.assign(`/service-estimate/new?id=${v.id}`); }}
                  style={{
                    border: isCurrent ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius:10, padding:14, background: isCurrent ? '#eff6ff' : '#fff',
                    cursor: isCurrent ? 'default' : 'pointer',
                    transition:'all 0.15s',
                  }}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <span className={`badge ${isCurrent ? 'badge-blue' : 'badge-gray'}`}>
                      v{v.version}{isCurrent && ' (현재)'}
                    </span>
                    <span style={{fontSize:11,color:'#94a3b8'}}>{v.created_at?.slice(0,16).replace('T',' ')}</span>
                  </div>
                  <div style={{fontSize:12,color:'#64748b',marginBottom:6,minHeight:32,lineHeight:1.4}}>
                    {itemSummary}
                  </div>
                  {v.version_note && <div style={{fontSize:11,color:'#475569',fontStyle:'italic',marginBottom:6}}>“{v.version_note}”</div>}
                  <div style={{fontSize:15,fontWeight:700,color:'#0f172a'}}>
                    ₩ {Number(v.total_amount || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 수신처 검색 모달 */}
      {showReceiverSearch && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowReceiverSearch(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:600,maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:12,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>수신처 검색</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowReceiverSearch(false)}>✕</span>
            </div>
            <div style={{display:'flex',gap:8,marginBottom:10}}>
              <input className="form-input" style={{flex:1}} value={receiverSearchQuery} onChange={e=>setReceiverSearchQuery(e.target.value)}
                onKeyDown={e=>{ if (e.key==='Enter') searchReceiver(); }} placeholder="회사명·담당자명 검색" autoFocus />
              <button className="btn btn-primary btn-sm" onClick={searchReceiver}>검색</button>
            </div>
            <table style={{fontSize:12}}>
              <thead><tr><th>회사명</th><th>담당자</th><th style={{width:90}}>전화</th><th style={{width:60}}></th></tr></thead>
              <tbody>
                {receiverSearchResults.map(c => (
                  <tr key={c.id}>
                    <td className="fw-bold">{c.name}</td>
                    <td>{c.contact_name||'-'}</td>
                    <td>{c.phone||'-'}</td>
                    <td><button className="btn btn-primary btn-sm" onClick={()=>{ selectCustomer(c); setShowReceiverSearch(false); }}>선택</button></td>
                  </tr>
                ))}
                {receiverSearchResults.length===0 && <tr><td colSpan={4} style={{textAlign:'center',padding:24,color:'#94a3b8'}}>검색 결과 없음</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 서비스 패키지 선택 모달 */}
      {showAddPkg && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowAddPkg(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:700,maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:12,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>서비스 패키지 선택</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowAddPkg(false)}>✕</span>
            </div>
            <input className="form-input" style={{marginBottom:10}} value={pkgSearch} onChange={e=>setPkgSearch(e.target.value)} placeholder="이름·코드 검색" autoFocus />
            <table style={{fontSize:12}}>
              <thead><tr><th style={{width:80}}>코드</th><th>이름</th><th style={{width:100}}>분류</th><th style={{width:60,textAlign:'center'}}>Task</th><th style={{width:60}}></th></tr></thead>
              <tbody>
                {filteredPkgs.map(p => (
                  <tr key={p.id}>
                    <td style={{fontFamily:'monospace'}}>{p.code}</td>
                    <td className="fw-bold">{p.name}</td>
                    <td>{p.category||'-'}</td>
                    <td style={{textAlign:'center'}}>{p.task_count||0}</td>
                    <td><button className="btn btn-primary btn-sm" onClick={()=>addPackage(p)}>추가</button></td>
                  </tr>
                ))}
                {filteredPkgs.length===0 && <tr><td colSpan={5} style={{textAlign:'center',padding:24,color:'#94a3b8'}}>검색 결과 없음</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
